// Import proxy client.
//
// Relays a third-party SaaS API call through func-dagrun-prod's
// POST /api/import/proxy endpoint so the browser isn't blocked by CORS.
// proxyFetch is shaped like the native fetch() so connectors can swap
// `fetch` → `proxyFetch` with no other changes.

const PROXY_URL = process.env.REACT_APP_IMPORT_PROXY_URL;
const PROXY_SECRET = process.env.REACT_APP_IMPORT_PROXY_SECRET;

function toHeaderRecord(init?: HeadersInit): Record<string, string> {
  const out: Record<string, string> = {};
  if (!init) return out;
  if (init instanceof Headers) {
    init.forEach((value, key) => { out[key] = value; });
  } else if (Array.isArray(init)) {
    init.forEach(([key, value]) => { out[key] = value; });
  } else {
    Object.assign(out, init);
  }
  return out;
}

/**
 * Drop-in replacement for fetch() that routes the call through the import
 * proxy. Returns a Response shaped identically to a native fetch Response —
 * the relayed upstream status, content-type, and body verbatim — so callers
 * consume the result unchanged.
 *
 * Throws on proxy-layer failures (auth/validation/allowlist → the proxy error
 * message; upstream timeout/network → the upstream error detail). Relayed
 * upstream responses (including upstream 4xx like a bad token) are returned as
 * a Response for the caller's own res.ok / res.status handling.
 */
export async function proxyFetch(url: string, options?: RequestInit): Promise<Response> {
  if (!PROXY_URL) {
    console.warn('Import proxy URL not configured — attempting direct fetch (may fail due to CORS)');
    return fetch(url, options);
  }

  const method = (options?.method ?? 'GET').toUpperCase();
  const headers = toHeaderRecord(options?.headers);
  const body = typeof options?.body === 'string' ? options.body : undefined;

  const proxyResp = await fetch(`${PROXY_URL.replace(/\/$/, '')}/api/import/proxy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Dagrun-Proxy-Key': PROXY_SECRET ?? '',
    },
    body: JSON.stringify({ url, method, headers, body }),
  });

  // Proxy-layer error (marked by the function) → throw.
  if (proxyResp.headers.get('X-Dagrun-Proxy-Error')) {
    let message = `Import proxy error (${proxyResp.status})`;
    try {
      const data = await proxyResp.json();
      message = data.detail ?? data.error ?? message;
    } catch { /* keep default */ }
    throw new Error(message);
  }

  // Verbatim-relayed upstream response → rebuild a native-shaped Response.
  const text = await proxyResp.text();
  const contentType = proxyResp.headers.get('content-type') ?? 'application/octet-stream';
  return new Response(text, {
    status: proxyResp.status,
    statusText: proxyResp.statusText,
    headers: { 'Content-Type': contentType },
  });
}
